import * as THREE from 'three';
import { Measure } from './utils/Measure';
import { PolygonClipVolume } from './utils/PolygonClipVolume';
import { Volume } from './utils/Volume';
import { XHRFactory } from './XHRFactory';
import { resourcePath } from './potreeState';

// Declare external globals used by legacy Potree code
declare const shapefile: {
  open(file: string): Promise<{
    read(): Promise<{ done: boolean; value?: { type: string; geometry?: unknown } }>;
  }>;
};
declare const TWEEN: {
  Easing: { Quartic: { Out: unknown } };
  Tween: new (target: Record<string, unknown>) => {
    to(target: Record<string, unknown>, duration: number): TweenInstance;
    easing(fn: unknown): TweenInstance;
    onUpdate(fn: () => void): TweenInstance;
    onComplete(fn: () => void): TweenInstance;
    start(): TweenInstance;
  };
};
interface TweenInstance {
  to(target: Record<string, unknown>, duration: number): TweenInstance;
  easing(fn: unknown): TweenInstance;
  onUpdate(fn: () => void): TweenInstance;
  onComplete(fn: () => void): TweenInstance;
  start(): TweenInstance;
  stop(): void;
}
declare const turf: {
  multiPoint(coords: number[][]): TurfFeature;
  polygon(coords: number[][][]): TurfFeature;
  convex(feature: TurfFeature): TurfFeature | null;
  intersect(collection: TurfFeatureCollection): TurfFeature | null;
  featureCollection(features: TurfFeature[]): TurfFeatureCollection;
};
interface TurfFeature {
  type: string;
  geometry: { type: string; coordinates: number[][][] };
  properties: Record<string, unknown>;
}
interface TurfFeatureCollection {
  type: string;
  features: TurfFeature[];
}
declare const proj4: {
  (from: string, to: string): Proj4Transform;
  defs(name: string, projection?: string): void;
};
interface Proj4Transform {
  forward(coords: number[]): number[];
  inverse(coords: number[]): number[];
}

/** Point with x, y coordinates */
interface Point2D {
  x: number;
  y: number;
}

/** Point with x, y, z coordinates */
interface Point3D extends Point2D {
  z: number;
}

/** Minimal viewer shape for pick operations */
export interface PickViewerBase {
  renderer: THREE.WebGLRenderer;
  pRenderer: {
    renderOctree(octree: unknown, nodes: unknown[], camera: THREE.Camera, target: THREE.WebGLRenderTarget | null, params?: Record<string, unknown>): void;
  };
}

/** Potree viewer interface - structurally compatible with Viewer */
interface PotreeViewer extends PickViewerBase {
  scene: PotreeViewerScene;
}

/** Scene type for PotreeViewer - extracted to avoid index signature issues with classes */
type PotreeViewerScene = {
  view: {
    position: THREE.Vector3;
    lookAt(target: THREE.Vector3): void;
  };
  getActiveCamera(): THREE.Camera;
  pointclouds: PotreePointCloud[];
};

/** Segment entry in a point cloud's segment list */
interface SegmentListEntry {
  boundingBox?: { p1: Point3D; p2: Point3D };
}

/** Potree point cloud - structurally compatible with PointCloudOctree */
export interface PotreePointCloud {
  root: PotreeNode | null;
  pick(viewer: PickViewerBase, camera: THREE.Camera, ray: THREE.Ray, params?: PickParams): PickResult | (PickResult | undefined)[] | Partial<PickResult> | (Partial<PickResult> | undefined)[] | null | undefined;
  identifier?: string;
  segmentList?: unknown[];
  visibleNodes?: PotreeNode[];
  nodesOnRay?(nodes: PotreeNode[], ray: THREE.Ray): PotreeNode[];
}

/**
 * Geometry node type compatible with PointCloudOctreeGeometryNode.
 * Uses a type alias (not interface) so that classes without explicit index
 * signatures satisfy this via structural compatibility.
 */
type PotreeGeometryNodeLike = {
  geometry: THREE.BufferGeometry | null;
};

/**
 * Potree tree node - structurally compatible with PointCloudOctreeNode and PointCloudOctreeGeometryNode.
 * Uses a type alias (not interface) so that classes without explicit index
 * signatures satisfy this via structural compatibility.
 */
type PotreeNode = {
  geometryNode?: PotreeGeometryNodeLike | null;
  sceneNode?: { matrixWorld: THREE.Matrix4 } | null;
  children?: unknown;
};

/** Helper to access potree-specific attribute data from a buffer geometry */
interface PotreeBufferAttributeData {
  array: Float32Array | Uint8Array;
  potree?: { range: [number, number] };
}

/** Pick parameters */
interface PickParams {
  all?: boolean;
  pickWindowSize?: number;
  pickClipped?: boolean;
  x?: number;
  y?: number;
}

/** Pick result */
interface PickResult {
  position: THREE.Vector3;
  distanceToRay: number;
}

/** Mouse intersection result */
interface MouseIntersection {
  location: THREE.Vector3;
  distance: number;
  pointcloud: PotreePointCloud;
  point: PickResult;
}

/** Color object with r, g, b components (0-1 range) */
interface ColorRGB {
  r: number;
  g: number;
  b: number;
}

/** Potree scene object */
interface PotreeScene {
  view: {
    position: THREE.Vector3;
    lookAt(target: THREE.Vector3): void;
    /** Track a camera-motion tween for cancellation on view switches (see viewer/View.ts). */
    registerMotionTween(tween: TweenInstance): TweenInstance;
  };
  getActiveCamera(): THREE.Camera;
}

/** Camera with zoomTo method (Potree custom) */
interface ZoomableCamera extends THREE.Camera {
  zoomTo(node: THREE.Object3D, factor: number): void;
}

/** Frustum with planes */
interface PotreeFrustum {
  planes: THREE.Plane[];
}

/** Sphere */
interface PotreeSphere {
  center: THREE.Vector3;
  radius: number;
}

/** Gradient scheme entry: [offset, color] */
type GradientSchemeEntry = [number, THREE.Color];

/** Event dispatcher with _listeners */
interface PotreeDispatcher {
  _listeners?: Record<string, unknown[]>;
}

/** Options for toTurfObject */
interface TurfObjectOptions {
  type?: string;
}

/** Screen pass object for rendering */
interface ScreenPass {
  screenScene: THREE.Scene;
  screenQuad: THREE.Mesh;
  camera: THREE.Camera;
  render(renderer: THREE.WebGLRenderer, material: THREE.Material, target?: THREE.WebGLRenderTarget): void;
}

export class Utils {
  static screenPass: ScreenPass;

  static async loadShapefileFeatures(file: string, callback: (features: { type: string; geometry?: unknown }[]) => void) {
    const features: { type: string; geometry?: unknown }[] = [];

    const handleFinish = () => {
      callback(features);
    };

    const source = await shapefile.open(file);

    while (true) {
      const result = await source.read();

      if (result.done) {
        handleFinish();
        break;
      }

      if (result.value && result.value.type === 'Feature' && result.value.geometry !== undefined) {
        features.push(result.value);
      }
    }
  }

  static toString(value: Point3D | string | number) {
    if (typeof value === 'object' && 'x' in value && value.x != null) {
      return value.x.toFixed(2) + ', ' + value.y.toFixed(2) + ', ' + value.z.toFixed(2);
    } else {
      return '' + value + '';
    }
  }

  static normalizeURL(url: string) {
    const u = new URL(url);

    return u.protocol + '//' + u.hostname + u.pathname.replace(/\/+/g, '/');
  }

  static pathExists(url: string) {
    const req = XHRFactory.createXMLHttpRequest();
    req.open('GET', url, false);
    req.send(null);
    if (req.status !== 200) {
      return false;
    }
    return true;
  }

  static debugSphere(parent: THREE.Object3D, position: THREE.Vector3, scale: number, color?: number) {
    const geometry = new THREE.SphereGeometry(1, 8, 8);
    let material: THREE.Material;

    if (color !== undefined) {
      material = new THREE.MeshBasicMaterial({ color: color });
    } else {
      material = new THREE.MeshNormalMaterial();
    }
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(position);
    sphere.scale.set(scale, scale, scale);
    parent.add(sphere);

    return sphere;
  }

  static debugLine(parent: THREE.Object3D, start: THREE.Vector3, end: THREE.Vector3, color: number) {
    const material = new THREE.LineBasicMaterial({ color: color });
    const geometry = new THREE.BufferGeometry();

    const positions = new Float32Array([0, 0, 0, end.x - start.x, end.y - start.y, end.z - start.z]);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const tl = new THREE.Line(geometry, material);
    tl.position.copy(start);

    parent.add(tl);

    const line = {
      node: tl,
      set: (newStart: THREE.Vector3, newEnd: THREE.Vector3) => {
        const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
        posAttr.setXYZ(0, newStart.x, newStart.y, newStart.z);
        posAttr.setXYZ(1, newEnd.x, newEnd.y, newEnd.z);
        posAttr.needsUpdate = true;
      },
    };

    return line;
  }

  static debugCircle(parent: THREE.Object3D, center: THREE.Vector3, radius: number, _normal: THREE.Vector3, color: number) {
    const material = new THREE.LineBasicMaterial({ color: color });

    const n = 32;
    const positions: number[] = [];
    for (let i = 0; i <= n; i++) {
      const u0 = 2 * Math.PI * (i / n);
      const u1 = (2 * Math.PI * (i + 1)) / n;

      positions.push(Math.cos(u0), Math.sin(u0), 0);
      positions.push(Math.cos(u1), Math.sin(u1), 0);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const tl = new THREE.Line(geometry, material);
    tl.position.copy(center);
    tl.scale.set(radius, radius, radius);

    parent.add(tl);
  }

  static debugBox(parent: THREE.Object3D, box: THREE.Box3, transform = new THREE.Matrix4(), color = 0xffff00) {
    const vertices = [
      [box.min.x, box.min.y, box.min.z],
      [box.min.x, box.min.y, box.max.z],
      [box.min.x, box.max.y, box.min.z],
      [box.min.x, box.max.y, box.max.z],

      [box.max.x, box.min.y, box.min.z],
      [box.max.x, box.min.y, box.max.z],
      [box.max.x, box.max.y, box.min.z],
      [box.max.x, box.max.y, box.max.z],
    ].map((v) => new THREE.Vector3(...(v as [number, number, number])));

    const edges = [
      [0, 4],
      [4, 5],
      [5, 1],
      [1, 0],
      [2, 6],
      [6, 7],
      [7, 3],
      [3, 2],
      [0, 2],
      [4, 6],
      [5, 7],
      [1, 3],
    ];

    const center = box.getCenter(new THREE.Vector3());

    const centroids = [
      { position: [box.min.x, center.y, center.z] as [number, number, number], color: 0xff0000 },
      { position: [box.max.x, center.y, center.z] as [number, number, number], color: 0x880000 },

      { position: [center.x, box.min.y, center.z] as [number, number, number], color: 0x00ff00 },
      { position: [center.x, box.max.y, center.z] as [number, number, number], color: 0x008800 },

      { position: [center.x, center.y, box.min.z] as [number, number, number], color: 0x0000ff },
      { position: [center.x, center.y, box.max.z] as [number, number, number], color: 0x000088 },
    ];

    for (const vertex of vertices) {
      const pos = vertex.clone().applyMatrix4(transform);

      Utils.debugSphere(parent, pos, 0.1, 0xff0000);
    }

    for (const edge of edges) {
      const start = vertices[edge[0]].clone().applyMatrix4(transform);
      const end = vertices[edge[1]].clone().applyMatrix4(transform);

      Utils.debugLine(parent, start, end, color);
    }

    for (const centroid of centroids) {
      const pos = new THREE.Vector3(...centroid.position).applyMatrix4(transform);

      Utils.debugSphere(parent, pos, 0.1, centroid.color);
    }
  }

  static debugPlane(parent: THREE.Object3D, plane: THREE.Plane, size = 1, color = 0x0000ff) {
    const planehelper = new THREE.PlaneHelper(plane, size, color);

    parent.add(planehelper);
  }

  /**
   * adapted from mhluska at https://github.com/mrdoob/three.js/issues/1561
   */
  static computeTransformedBoundingBox(box: THREE.Box3, transform: THREE.Matrix4) {
    const vertices = [
      new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.min.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.max.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.min.y, box.max.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.max.y, box.max.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.max.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.min.y, box.max.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.max.y, box.max.z).applyMatrix4(transform),
    ];

    const boundingBox = new THREE.Box3();
    boundingBox.setFromPoints(vertices);

    return boundingBox;
  }

  /**
   * add separators to large numbers
   */
  static addCommas(nStr: string | number) {
    nStr += '';
    const x = (nStr as string).split('.');
    let x1 = x[0];
    const x2 = x.length > 1 ? '.' + x[1] : '';
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1,$2');
    }
    return x1 + x2;
  }

  static removeCommas(str: string) {
    return str.replace(/,/g, '');
  }

  /**
   * create worker from a string
   *
   * code from http://stackoverflow.com/questions/10343913/how-to-create-a-web-worker-from-a-string
   */
  static createWorker(code: string) {
    const blob = new Blob([code], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));

    return worker;
  }

  static moveTo(scene: PotreeScene, endPosition: THREE.Vector3, endTarget: THREE.Vector3) {
    const view = scene.view;
    const camera = scene.getActiveCamera();
    const animationDuration = 500;
    const easing = TWEEN.Easing.Quartic.Out;

    {
      // animate camera position — registered on the view so a view switch
      // can cancel it (otherwise it keeps writing the pose for 500ms after).
      const tween = view.registerMotionTween(new TWEEN.Tween(view.position as unknown as Record<string, unknown>).to(endPosition as unknown as Record<string, unknown>, animationDuration));
      tween.easing(easing);
      tween.start();
    }

    {
      // animate camera target
      const camTargetDistance = camera.position.distanceTo(endTarget);
      const target = new THREE.Vector3().addVectors(camera.position, camera.getWorldDirection(new THREE.Vector3()).clone().multiplyScalar(camTargetDistance));
      const tween = view.registerMotionTween(new TWEEN.Tween(target as unknown as Record<string, unknown>).to(endTarget as unknown as Record<string, unknown>, animationDuration));
      tween.easing(easing);
      tween.onUpdate(() => {
        view.lookAt(target);
      });
      tween.onComplete(() => {
        view.lookAt(target);
      });
      tween.start();
    }
  }

  static loadSkybox(path: string) {
    const parent = new THREE.Object3D();
    parent.name = 'skybox_root';

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 1, 100000);
    camera.up.set(0, 0, 1);
    const scene = new THREE.Scene();

    const format = '.jpg';
    const urls = [path + 'px' + format, path + 'nx' + format, path + 'py' + format, path + 'ny' + format, path + 'pz' + format, path + 'nz' + format];

    const materialArray: THREE.MeshBasicMaterial[] = [];
    for (let i = 0; i < 6; i++) {
      const material = new THREE.MeshBasicMaterial({
        map: null,
        side: THREE.BackSide,
        depthTest: false,
        depthWrite: false,
        color: 0x424556,
      });

      materialArray.push(material);

      const loader = new THREE.TextureLoader();
      loader.load(
        urls[i],
        function loaded(texture: THREE.Texture) {
          material.map = texture;
          material.needsUpdate = true;
          material.color.setHex(0xffffff);
        },
        function progress(_xhr: ProgressEvent) {
          // console.log( (xhr.loaded / xhr.total * 100) + '% loaded' );
        },
        function error(err: unknown) {
          console.error('An error happened', err);
        },
      );
    }

    const skyGeometry = new THREE.BoxGeometry(700, 700, 700);
    const skybox = new THREE.Mesh(skyGeometry, materialArray);

    scene.add(skybox);

    scene.traverse((n) => (n.frustumCulled = false));

    // z up
    scene.rotation.x = Math.PI / 2;

    parent.children.push(camera);
    camera.parent = parent;

    return { camera, scene, parent };
  }

  static createGrid(width: number, length: number, spacing: number, color?: number) {
    const material = new THREE.LineBasicMaterial({
      color: color || 0x888888,
    });

    const positions: number[] = [];
    for (let i = 0; i <= length; i++) {
      positions.push(-(spacing * width) / 2, i * spacing - (spacing * length) / 2, 0);
      positions.push(+(spacing * width) / 2, i * spacing - (spacing * length) / 2, 0);
    }

    for (let i = 0; i <= width; i++) {
      positions.push(i * spacing - (spacing * width) / 2, -(spacing * length) / 2, 0);
      positions.push(i * spacing - (spacing * width) / 2, +(spacing * length) / 2, 0);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const line = new THREE.LineSegments(geometry, material);
    line.receiveShadow = true;
    return line;
  }

  static createBackgroundTexture(width: number, height: number) {
    function gauss(x: number, y: number) {
      return (1 / (2 * Math.PI)) * Math.exp(-(x * x + y * y) / 2);
    }

    // map.magFilter = THREE.NearestFilter;
    const size = width * height;
    const data = new Uint8Array(3 * size);

    const chroma = [1, 1.5, 1.7];
    const max = gauss(0, 0);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const u = 2 * (x / width) - 1;
        const v = 2 * (y / height) - 1;

        const i = x + width * y;
        const d = gauss(2 * u, 2 * v) / max;
        let r = (Math.random() + Math.random() + Math.random()) / 3;
        r = (d * 0.5 + 0.5) * r * 0.03;
        r = r * 0.4;

        data[3 * i + 0] = 255 * (d / 15 + 0.05 + r) * chroma[0];
        data[3 * i + 1] = 255 * (d / 15 + 0.05 + r) * chroma[1];
        data[3 * i + 2] = 255 * (d / 15 + 0.05 + r) * chroma[2];
      }
    }

    const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
    texture.needsUpdate = true;

    return texture;
  }

  static getMousePointCloudIntersection(mouse: Point2D, camera: THREE.Camera, viewer: PickViewerBase, pointclouds: PotreePointCloud[], params: PickParams = {}): MouseIntersection | PickResult[] | null {
    const renderer = viewer.renderer;

    const nmouse = {
      x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
      y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
    };

    const pickParams: PickParams = {};

    if (params.all) {
      pickParams.all = params.all;
      pickParams.pickWindowSize = params.pickWindowSize;
    }

    if (params.pickClipped) {
      pickParams.pickClipped = params.pickClipped;
    }

    pickParams.x = mouse.x;
    pickParams.y = renderer.domElement.clientHeight - mouse.y;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(nmouse.x, nmouse.y), camera);
    const ray = raycaster.ray;

    if (pickParams.all) {
      return pointclouds.flatMap((pointcloud) => (pointcloud.pick(viewer, camera, ray, pickParams) as PickResult[]) || []);
    }

    let selectedPointcloud: PotreePointCloud | null = null;
    let closestDistanceToRay = Infinity;
    let closestDistanceToCamera = Infinity;
    let closestIntersection: THREE.Vector3 | null = null;
    let closestPoint: PickResult | null = null;

    for (const pointcloud of pointclouds) {
      const point = pointcloud.pick(viewer, camera, ray, pickParams) as PickResult | null;

      if (!point) {
        continue;
      }

      const distanceToCamera = camera.position.distanceTo(point.position);
      const distanceToRay = point.distanceToRay;

      if (distanceToRay < closestDistanceToRay || (distanceToRay === closestDistanceToRay && distanceToCamera < closestDistanceToCamera)) {
        closestDistanceToRay = distanceToRay;
        closestDistanceToCamera = distanceToCamera;
        selectedPointcloud = pointcloud;
        closestIntersection = point.position;
        closestPoint = point;
      }
    }

    if (selectedPointcloud && closestIntersection && closestPoint) {
      return {
        location: closestIntersection,
        distance: closestDistanceToCamera,
        pointcloud: selectedPointcloud,
        point: closestPoint,
      };
    } else {
      return null;
    }
  }

  /**
   * Converts an array of THREE.Vector2 points to a Turf.js polygon or multiPoint feature.
   */
  static toTurfObject(points: Point2D[], options: TurfObjectOptions = { type: 'polygon' }) {
    const coordinates = points.map((point) => {
      return [point.x, point.y];
    });

    if (options.type === 'multipoint') {
      return turf.multiPoint(coordinates);
    } else {
      // Conditionally close the polygon
      if (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || coordinates[0][1] !== coordinates[coordinates.length - 1][1]) {
        coordinates.push(coordinates[0]);
      }

      const polygon = turf.polygon([coordinates]);
      return polygon;
    }
  }

  /**
   * Returns all segments that are sufficiently inside the polygon.
   */
  static getPolygonPointCloudIntersection(polygon: TurfFeature, camera: THREE.Camera, viewer: PotreeViewer, pointclouds: PotreePointCloud[], params: { threshold: number } = { threshold: 1 }) {
    function isBoundingBoxInsidePolygon(boundingBox: { p1: Point3D; p2: Point3D }) {
      let hullArea = 0;
      let intersectionArea = 0;
      let ratio = 0;

      // Project a 3D point to screen coordinates.
      function projectToScreen(point: THREE.Vector3, cam: THREE.Camera) {
        const clientWidth = viewer.renderer.domElement.clientWidth;
        const clientHeight = viewer.renderer.domElement.clientHeight;

        const vector = point.clone().project(cam);

        const x = Math.round(((vector.x + 1) * clientWidth) / 2);
        const y = Math.round(((-vector.y + 1) * clientHeight) / 2);

        return new THREE.Vector2(x, y);
      }

      // Calculates planar area of polygon (Shoelace formula)
      function polygonArea(poly: TurfFeature) {
        const coords = poly.geometry.coordinates[0];
        let area = 0;

        for (let i = 0; i < coords.length - 1; i++) {
          area += coords[i][0] * coords[i + 1][1] - coords[i + 1][0] * coords[i][1];
        }

        return Math.abs(area / 2);
      }

      // Project each corner of the bounding box into 2D screen space & create a turf feature from them.
      const projectedCorners = () => {
        const { p1, p2 } = boundingBox;

        // Compute the 8 corners of the bounding box.
        const corners = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => new THREE.Vector3(i & 4 ? p2.x : p1.x, i & 2 ? p2.y : p1.y, i & 1 ? p2.z : p1.z));

        return Utils.toTurfObject(
          corners.map((corner) => projectToScreen(corner, camera)),
          { type: 'multiPoint' },
        );
      };

      // Compute the convex hull of the projected corners.
      const hull = turf.convex(projectedCorners());
      if (hull) {
        hullArea = polygonArea(hull);
      } else {
        // can't form a valid hull from the bounding box
        return 0;
      }

      try {
        // Compute the areas to check what fraction of the bounding box is inside.
        const intersection = turf.intersect(turf.featureCollection([hull, polygon]));
        if (intersection && hullArea > 0) {
          intersectionArea = polygonArea(intersection);
          ratio = intersectionArea / hullArea;
        }
      } catch (e) {
        console.error(e);
      }
      return ratio >= params.threshold;
    }

    return pointclouds.map((pointcloud) => {
      // not sure why some bounding boxes have infinity coordinates
      const validCoord = (coord: Point3D) => coord.x !== Infinity && coord.y !== Infinity && coord.z !== Infinity;
      const segmentIdList = (pointcloud.segmentList ?? []).reduce((acc: number[], _segment, index) => {
        const segment = _segment as SegmentListEntry;
        if (segment.boundingBox && validCoord(segment.boundingBox.p1) && validCoord(segment.boundingBox.p2) && isBoundingBoxInsidePolygon(segment.boundingBox)) {
          acc.push(index);
        }
        return acc;
      }, []);

      return { pointcloudId: pointcloud.identifier, segmentIdList };
    });
  }

  static pixelsArrayToImage(pixels: Uint8ClampedArray, width: number, height: number) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return new Image();

    const pixelsCopy = new Uint8ClampedArray(pixels);

    for (let i = 0; i < pixelsCopy.length; i++) {
      pixelsCopy[i * 4 + 3] = 255;
    }

    const imageData = context.createImageData(width, height);
    imageData.data.set(pixelsCopy);
    context.putImageData(imageData, 0, 0);

    const img = new Image();
    img.src = canvas.toDataURL();

    return img;
  }

  static pixelsArrayToDataUrl(pixels: Uint8ClampedArray, width: number, height: number) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return '';

    const pixelsCopy = new Uint8ClampedArray(pixels);

    for (let i = 0; i < pixelsCopy.length; i++) {
      pixelsCopy[i * 4 + 3] = 255;
    }

    const imageData = context.createImageData(width, height);
    imageData.data.set(pixelsCopy);
    context.putImageData(imageData, 0, 0);

    const dataURL = canvas.toDataURL();

    return dataURL;
  }

  static pixelsArrayToCanvas(pixels: Uint8ClampedArray, width: number, height: number) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return canvas;

    const pixelsCopy = new Uint8ClampedArray(pixels);

    // flip vertically
    const bytesPerLine = width * 4;
    for (let i = 0; i < Math.floor(height / 2); i++) {
      const j = height - i - 1;

      const lineI = pixelsCopy.slice(i * bytesPerLine, i * bytesPerLine + bytesPerLine);
      const lineJ = pixelsCopy.slice(j * bytesPerLine, j * bytesPerLine + bytesPerLine);
      pixelsCopy.set(lineJ, i * bytesPerLine);
      pixelsCopy.set(lineI, j * bytesPerLine);
    }

    const imageData = context.createImageData(width, height);
    imageData.data.set(pixelsCopy);
    context.putImageData(imageData, 0, 0);

    return canvas;
  }

  static removeListeners(dispatcher: PotreeDispatcher, type: string) {
    if (dispatcher._listeners === undefined) {
      return;
    }

    if (dispatcher._listeners[type]) {
      delete dispatcher._listeners[type];
    }
  }

  static mouseToRay(mouse: Point2D, camera: THREE.Camera, width: number, height: number) {
    const normalizedMouse = {
      x: (mouse.x / width) * 2 - 1,
      y: -(mouse.y / height) * 2 + 1,
    };

    const vector = new THREE.Vector3(normalizedMouse.x, normalizedMouse.y, 0.5);
    const origin = camera.position.clone();
    vector.unproject(camera);
    const direction = new THREE.Vector3().subVectors(vector, origin).normalize();

    const ray = new THREE.Ray(origin, direction);

    return ray;
  }

  static projectedRadius(radius: number, camera: THREE.Camera, distance: number, screenWidth: number, screenHeight: number) {
    if (camera instanceof THREE.OrthographicCamera) {
      return Utils.projectedRadiusOrtho(radius, camera.projectionMatrix, screenWidth, screenHeight);
    } else if (camera instanceof THREE.PerspectiveCamera) {
      return Utils.projectedRadiusPerspective(radius, (camera.fov * Math.PI) / 180, distance, screenHeight);
    } else {
      throw new Error('invalid parameters');
    }
  }

  static projectedRadiusPerspective(radius: number, fov: number, distance: number, screenHeight: number) {
    let projFactor = 1 / Math.tan(fov / 2) / distance;
    projFactor = (projFactor * screenHeight) / 2;

    return radius * projFactor;
  }

  static projectedRadiusOrtho(radius: number, proj: THREE.Matrix4, screenWidth: number, screenHeight: number) {
    const p1v4 = new THREE.Vector4(0);
    const p2v4 = new THREE.Vector4(radius);

    p1v4.applyMatrix4(proj);
    p2v4.applyMatrix4(proj);
    const p1 = new THREE.Vector3(p1v4.x, p1v4.y, p1v4.z);
    const p2 = new THREE.Vector3(p2v4.x, p2v4.y, p2v4.z);
    p1.x = (p1.x + 1.0) * 0.5 * screenWidth;
    p1.y = (p1.y + 1.0) * 0.5 * screenHeight;
    p2.x = (p2.x + 1.0) * 0.5 * screenWidth;
    p2.y = (p2.y + 1.0) * 0.5 * screenHeight;
    return p1.distanceTo(p2);
  }

  static topView(camera: ZoomableCamera, node: THREE.Object3D) {
    camera.position.set(0, 1, 0);
    camera.rotation.set(-Math.PI / 2, 0, 0);
    camera.zoomTo(node, 1);
  }

  static frontView(camera: ZoomableCamera, node: THREE.Object3D) {
    camera.position.set(0, 0, 1);
    camera.rotation.set(0, 0, 0);
    camera.zoomTo(node, 1);
  }

  static leftView(camera: ZoomableCamera, node: THREE.Object3D) {
    camera.position.set(-1, 0, 0);
    camera.rotation.set(0, -Math.PI / 2, 0);
    camera.zoomTo(node, 1);
  }

  static rightView(camera: ZoomableCamera, node: THREE.Object3D) {
    camera.position.set(1, 0, 0);
    camera.rotation.set(0, Math.PI / 2, 0);
    camera.zoomTo(node, 1);
  }

  static findClosestGpsTime(target: number, viewer: PickViewerBase & { scene: { pointclouds: PotreePointCloud[] } }) {
    const nodes: PotreeNode[] = [];
    for (const pc of viewer.scene.pointclouds) {
      if (!pc.root) continue;
      nodes.push(pc.root);

      const rootChildren = pc.root.children as Record<string | number, PotreeNode> | PotreeNode[] | undefined;
      if (rootChildren) {
        const childArray = Array.isArray(rootChildren) ? rootChildren : Object.values(rootChildren);
        for (const child of childArray) {
          if (child) {
            nodes.push(child);
          }
        }
      }
    }

    let closestNode: PotreeNode | null = null;
    let closestIndex = Infinity;
    let closestDistance = Infinity;

    for (const node of nodes) {
      if (!node.geometryNode?.geometry || !node.sceneNode) {
        continue;
      }

      const geometry = node.geometryNode.geometry;
      const gpsTime = geometry.attributes['gps-time'] as unknown as PotreeBufferAttributeData;
      const range = gpsTime.potree?.range;
      if (!range) continue;

      for (let i = 0; i < gpsTime.array.length; i++) {
        let value = gpsTime.array[i];
        value = value * (range[1] - range[0]) + range[0];
        const distance = Math.abs(target - value);

        if (distance < closestDistance) {
          closestIndex = i;
          closestDistance = distance;
          closestNode = node;
        }
      }
    }

    if (!closestNode?.geometryNode?.geometry || !closestNode.sceneNode) {
      return { node: closestNode, index: closestIndex, position: new THREE.Vector3() };
    }

    const closestGeometry = closestNode.geometryNode.geometry;
    const position = new THREE.Vector3(closestGeometry.attributes.position.array[3 * closestIndex + 0], closestGeometry.attributes.position.array[3 * closestIndex + 1], closestGeometry.attributes.position.array[3 * closestIndex + 2]);

    position.applyMatrix4(closestNode.sceneNode.matrixWorld);

    return {
      node: closestNode,
      index: closestIndex,
      position: position,
    };
  }

  /**
   *
   * 0: no intersection
   * 1: intersection
   * 2: fully inside
   */
  static frustumSphereIntersection(frustum: PotreeFrustum, sphere: PotreeSphere) {
    const planes = frustum.planes;
    const center = sphere.center;
    const negRadius = -sphere.radius;

    let minDistance = Number.MAX_VALUE;

    for (let i = 0; i < 6; i++) {
      const distance = planes[i].distanceToPoint(center);

      if (distance < negRadius) {
        return 0;
      }

      minDistance = Math.min(minDistance, distance);
    }

    return minDistance >= sphere.radius ? 2 : 1;
  }

  // code taken from three.js
  // ImageUtils - generateDataTexture()
  static generateDataTexture(width: number, height: number, color: ColorRGB) {
    const size = width * height;
    const data = new Uint8Array(4 * width * height);

    const r = Math.floor(color.r * 255);
    const g = Math.floor(color.g * 255);
    const b = Math.floor(color.b * 255);

    for (let i = 0; i < size; i++) {
      data[i * 3] = r;
      data[i * 3 + 1] = g;
      data[i * 3 + 2] = b;
    }

    const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
    texture.needsUpdate = true;
    texture.magFilter = THREE.NearestFilter;

    return texture;
  }

  // from http://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
  static getParameterByName(name: string) {
    name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(document.location.search);
    return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }

  static setParameter(name: string, value: string) {
    name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('([\\?&])(' + name + '=([^&#]*))');
    const results = regex.exec(document.location.search);

    let url = window.location.href;
    if (results === null) {
      if (window.location.search.length === 0) {
        url = url + '?';
      } else {
        url = url + '&';
      }

      url = url + name + '=' + value;
    } else {
      const newValue = name + '=' + value;
      url = url.replace(results[2], newValue);
    }
    window.history.replaceState({}, '', url);
  }

  static createChildAABB(aabb: THREE.Box3, index: number) {
    const min = aabb.min.clone();
    const max = aabb.max.clone();
    const size = new THREE.Vector3().subVectors(max, min);

    if ((index & 0b0001) > 0) {
      min.z += size.z / 2;
    } else {
      max.z -= size.z / 2;
    }

    if ((index & 0b0010) > 0) {
      min.y += size.y / 2;
    } else {
      max.y -= size.y / 2;
    }

    if ((index & 0b0100) > 0) {
      min.x += size.x / 2;
    } else {
      max.x -= size.x / 2;
    }

    return new THREE.Box3(min, max);
  }

  // see https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
  static clipboardCopy(text: string) {
    const textArea = document.createElement('textarea');

    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';

    textArea.style.width = '2em';
    textArea.style.height = '2em';

    textArea.style.padding = '0';

    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';

    textArea.style.background = 'transparent';

    textArea.value = text;

    document.body.appendChild(textArea);

    textArea.select();

    try {
      document.execCommand('copy');
    } catch {
      // copy failed silently
    }

    document.body.removeChild(textArea);
  }

  static getMeasurementIcon(measurement: Measure | Volume | PolygonClipVolume): string | undefined {
    if (measurement instanceof Measure) {
      const m = measurement as Measure & { showAngles?: boolean; showHeight?: boolean };
      if (measurement.showDistances && !measurement.showArea && !m.showAngles) {
        return `${resourcePath}/icons/distance.svg`;
      } else if (measurement.showDistances && measurement.showArea && !m.showAngles) {
        return `${resourcePath}/icons/area.svg`;
      } else if (measurement.maxMarkers === 1) {
        return `${resourcePath}/icons/point.svg`;
      } else if (!measurement.showDistances && !measurement.showArea && m.showAngles) {
        return `${resourcePath}/icons/angle.png`;
      } else if (m.showHeight) {
        return `${resourcePath}/icons/height.svg`;
      } else {
        return `${resourcePath}/icons/distance.svg`;
      }
    } else if (measurement instanceof Volume) {
      return `${resourcePath}/icons/volume.svg`;
    } else if (measurement instanceof PolygonClipVolume) {
      return `${resourcePath}/icons/clip-polygon.svg`;
    }
  }

  static lineToLineIntersection(P0: THREE.Vector3, P1: THREE.Vector3, P2: THREE.Vector3, P3: THREE.Vector3) {
    const P = [P0, P1, P2, P3];

    const d = (m: number, n: number, o: number, p: number) => {
      const result = (P[m].x - P[n].x) * (P[o].x - P[p].x) + (P[m].y - P[n].y) * (P[o].y - P[p].y) + (P[m].z - P[n].z) * (P[o].z - P[p].z);

      return result;
    };

    const mua = (d(0, 2, 3, 2) * d(3, 2, 1, 0) - d(0, 2, 1, 0) * d(3, 2, 3, 2)) / (d(1, 0, 1, 0) * d(3, 2, 3, 2) - d(3, 2, 1, 0) * d(3, 2, 1, 0));

    const mub = (d(0, 2, 3, 2) + mua * d(3, 2, 1, 0)) / d(3, 2, 3, 2);

    const P01 = P1.clone().sub(P0);
    const P23 = P3.clone().sub(P2);

    const Pa = P0.clone().add(P01.multiplyScalar(mua));
    const Pb = P2.clone().add(P23.multiplyScalar(mub));

    const center = Pa.clone().add(Pb).multiplyScalar(0.5);

    return center;
  }

  static computeCircleCenter(A: THREE.Vector3, B: THREE.Vector3, C: THREE.Vector3) {
    const AB = B.clone().sub(A);
    const AC = C.clone().sub(A);

    const N = AC.clone().cross(AB).normalize();

    const ab_dir = AB.clone().cross(N).normalize();
    const ac_dir = AC.clone().cross(N).normalize();

    const ab_origin = A.clone().add(B).multiplyScalar(0.5);
    const ac_origin = A.clone().add(C).multiplyScalar(0.5);

    const P0 = ab_origin;
    const P1 = ab_origin.clone().add(ab_dir);

    const P2 = ac_origin;
    const P3 = ac_origin.clone().add(ac_dir);

    const center = Utils.lineToLineIntersection(P0, P1, P2, P3);

    return center;
  }

  static getNorthVec(p1: THREE.Vector3, distance: number, projection?: string) {
    if (projection) {
      proj4.defs('pointcloud', projection);
      const transform = proj4('pointcloud', 'WGS84');

      const llP1 = transform.forward(p1.toArray());
      let llP2 = transform.forward([p1.x, p1.y + distance]);
      const polarRadius = Math.sqrt((llP2[0] - llP1[0]) ** 2 + (llP2[1] - llP1[1]) ** 2);
      llP2 = [llP1[0], llP1[1] + polarRadius];

      const northVec = transform.inverse(llP2);

      return new THREE.Vector3(northVec[0], northVec[1], p1.z).sub(p1);
    } else {
      const vec = new THREE.Vector3(0, 1, 0).multiplyScalar(distance);

      return vec;
    }
  }

  static computeAzimuth(p1: THREE.Vector3, p2: THREE.Vector3, projection?: string) {
    let azimuth = 0;

    if (projection) {
      let transform: Proj4Transform;

      if (projection.includes('EPSG')) {
        transform = proj4(projection, 'WGS84');
      } else {
        proj4.defs('pointcloud', projection);
        transform = proj4('pointcloud', 'WGS84');
      }

      const llP1 = transform.forward(p1.toArray());
      const llP2 = transform.forward(p2.toArray());
      const dir = [llP2[0] - llP1[0], llP2[1] - llP1[1]];
      azimuth = Math.atan2(dir[1], dir[0]) - Math.PI / 2;
    } else {
      const dir = [p2.x - p1.x, p2.y - p1.y];
      azimuth = Math.atan2(dir[1], dir[0]) - Math.PI / 2;
    }

    // make clockwise
    azimuth = -azimuth;

    return azimuth;
  }

  static async loadScript(url: string) {
    return new Promise<void>((resolve) => {
      const element = document.getElementById(url);

      if (element) {
        resolve();
      } else {
        const script = document.createElement('script');

        script.id = url;

        script.onload = () => {
          resolve();
        };
        script.src = url;

        document.body.appendChild(script);
      }
    });
  }

  static createSvgGradient(scheme: GradientSchemeEntry[]) {
    const gradientId = `${Math.random()}_${Date.now()}`;

    const svgn = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgn, 'svg');
    svg.setAttributeNS(null, 'width', '2em');
    svg.setAttributeNS(null, 'height', '3em');

    {
      // <defs>
      const defs = document.createElementNS(svgn, 'defs');

      const linearGradient = document.createElementNS(svgn, 'linearGradient');
      linearGradient.setAttributeNS(null, 'id', gradientId);
      linearGradient.setAttributeNS(null, 'gradientTransform', 'rotate(90)');

      for (let i = scheme.length - 1; i >= 0; i--) {
        const stopVal = scheme[i];
        const percent = Math.floor(100 - stopVal[0] * 100);
        const [r, g, b] = stopVal[1].toArray().map((v) => Math.floor(v * 255));

        const stop = document.createElementNS(svgn, 'stop');
        stop.setAttributeNS(null, 'offset', `${percent}%`);
        stop.setAttributeNS(null, 'stop-color', `rgb(${r}, ${g}, ${b})`);

        linearGradient.appendChild(stop);
      }

      defs.appendChild(linearGradient);
      svg.appendChild(defs);
    }

    const rect = document.createElementNS(svgn, 'rect');
    rect.setAttributeNS(null, 'width', `100%`);
    rect.setAttributeNS(null, 'height', `100%`);
    rect.setAttributeNS(null, 'fill', `url("#${gradientId}")`);
    rect.setAttributeNS(null, 'stroke', `black`);
    rect.setAttributeNS(null, 'stroke-width', `0.1em`);

    svg.appendChild(rect);

    return svg;
  }

  static async waitAny(promises: Promise<unknown>[]) {
    return new Promise<void>((resolve) => {
      promises.forEach((promise) => {
        promise.then(() => {
          resolve();
        });
      });
    });
  }

  /**
   * Check intersection of two line segments p1->p2 and p3->p4.
   * Returns { x, y } of the intersection, or null if no intersection exists.
   */
  static getLineSegmentIntersection(p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D) {
    // Destructure points for convenience
    const { x: x1, y: y1 } = p1;
    const { x: x2, y: y2 } = p2;
    const { x: x3, y: y3 } = p3;
    const { x: x4, y: y4 } = p4;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (denom === 0) {
      return null;
    }

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = ((x1 - x3) * (y1 - y2) - (y1 - y3) * (x1 - x2)) / denom;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        x: x1 + t * (x2 - x1),
        y: y1 + t * (y2 - y1),
      };
    }

    return null;
  }

  /**
   * Takes an array of points representing a polyline (consecutive points form segments).
   * 1. Finds the first segment intersection.
   * 2. If such an intersection is found, returns a polygon that is formed by the first intersection.
   * 3. If no intersection is found by the end, returns the entire set of points plus a closing segment from last->first.
   */
  static simplifyPolygon(points: Point2D[]) {
    if (points.length < 3) {
      return points;
    }

    for (let i = 1; i < points.length; i++) {
      const currentStart = points[i - 1];
      const currentEnd = points[i];

      for (let j = 0; j < i - 1; j++) {
        const segStart = points[j];
        const segEnd = points[j + 1];

        let intersection: Point2D | null = null;
        if (Math.abs(i - j) > 2) {
          intersection = Utils.getLineSegmentIntersection(currentStart, currentEnd, segStart, segEnd);
        }

        if (intersection) {
          const result: Point2D[] = [intersection].concat(points.slice(j + 1, i));
          result.push(intersection);
          return result;
        }
      }
    }

    points.push(points[0]);
    return points;
  }

  /**
   * Changes the cursor icon based on the provided tool name.
   */
  static changeCursor(toolName = 'default') {
    const toolIcons = [
      { toolName: 'lasso', url: 'url(/potree/potree/resources/icons/cursor_lasso.svg) 5 24, default' },
      { toolName: 'orbit', url: 'url(/potree/potree/resources/icons/cursor_orbit.svg) 16 16, default' },
    ];

    const toolIcon = toolIcons.find((icon) => icon.toolName === toolName);
    if (toolIcon) {
      document.body.style.cursor = toolIcon.url;
    } else {
      document.body.style.cursor = toolName;
    }
  }
}

Utils.screenPass = (() => {
  const screenScene = new THREE.Scene();
  const screenQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2, 1));
  (screenQuad.material as THREE.MeshBasicMaterial).depthTest = true;
  (screenQuad.material as THREE.MeshBasicMaterial).depthWrite = true;
  (screenQuad.material as THREE.MeshBasicMaterial).transparent = true;
  screenScene.add(screenQuad);
  const camera = new THREE.Camera();

  return {
    screenScene,
    screenQuad,
    camera,
    render(renderer: THREE.WebGLRenderer, material: THREE.Material, target?: THREE.WebGLRenderTarget) {
      screenQuad.material = material;

      if (typeof target === 'undefined') {
        renderer.render(screenScene, camera);
      } else {
        renderer.setRenderTarget(target);
        renderer.render(screenScene, camera);
        renderer.setRenderTarget(null);
      }
    },
  };
})();
